import { useCallback, useEffect, useState } from 'react';
import * as api from '../services/ClusteringLabService';
import { ClusteringLabGroup, ClusteringLabMembership, ClusteringLabPhoto, ClusteringLabVisitCluster } from '../types';

interface ClusteringLabData {
  userLid: number | null;
  photos: ClusteringLabPhoto[];
  groups: ClusteringLabGroup[];
  memberships: ClusteringLabMembership[];
  visitClusters: ClusteringLabVisitCluster[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => void;
  setGroups: React.Dispatch<React.SetStateAction<ClusteringLabGroup[]>>;
  setMemberships: React.Dispatch<React.SetStateAction<ClusteringLabMembership[]>>;
}

export function useClusteringLabData(userLid: number | null): ClusteringLabData {
  const [photos, setPhotos] = useState<ClusteringLabPhoto[]>([]);
  const [groups, setGroups] = useState<ClusteringLabGroup[]>([]);
  const [memberships, setMemberships] = useState<ClusteringLabMembership[]>([]);
  const [visitClusters, setVisitClusters] = useState<ClusteringLabVisitCluster[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const loadInitial = useCallback(async () => {
    if (!userLid) return;
    try {
      setLoading(true);
      setError(null);

      const [photosRes, groupsRes] = await Promise.all([api.listPhotos(userLid), api.listGroups(userLid)]);
      if (!photosRes || !groupsRes) return;

      setPhotos(photosRes.photos);
      setMemberships(photosRes.memberships);
      setVisitClusters(photosRes.visit_clusters || []);
      setNextCursor(photosRes.next_cursor ?? null);
      setTotalCount(photosRes.total_count ?? 0);
      setGroups(groupsRes.groups);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [userLid]);

  const loadMore = useCallback(async () => {
    if (!userLid || !nextCursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const res = await api.listPhotos(userLid, nextCursor);
      if (!res) return;
      setPhotos((prev) => [...prev, ...res.photos]);
      setMemberships((prev) => {
        const existing = new Set(prev.map((m) => `${m.photo_id}:${m.group_id}`));
        const newOnes = res.memberships.filter((m) => !existing.has(`${m.photo_id}:${m.group_id}`));
        return [...prev, ...newOnes];
      });
      setVisitClusters((prev) => {
        const existing = new Set(prev.map((vc) => vc.visit_cluster_uuid));
        const newOnes = (res.visit_clusters || []).filter((vc) => !existing.has(vc.visit_cluster_uuid));
        return [...prev, ...newOnes];
      });
      setNextCursor(res.next_cursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [userLid, nextCursor, loadingMore]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  return {
    userLid,
    photos,
    groups,
    memberships,
    visitClusters,
    loading,
    loadingMore,
    error,
    hasMore: nextCursor !== null,
    totalCount,
    loadMore,
    setGroups,
    setMemberships,
  };
}
